import isEmpty from 'lodash/isEmpty'
import {
  ManualPrescriptionObject,
  PrescriptionFlow,
  PrescriptionObject,
  UploadPrescriptionObject,
} from '@/scripts/libs/rox-lens-panel/types/PrescriptionModule'
import WCSPrescription from '@/scripts/libs/wcs/prescription'
import { FALSE_LOWERCASE, TRUE_LOWERCASE } from '@/scripts/constants/booleans'
import {
  PRESCRIPTION_ENABLED,
  PRESCRIPTION_MANUAL_ENABLED,
  PRESCRIPTION_UPLOAD_ENABLED,
} from '@/elements/rox-panel/constants/prescription-cookies'
import {
  ORDER_ID_DEFAULT_VALUE,
  ORDER_ITEM_ID_DEFAULT_VALUE,
} from '@/elements/prescription/constants/prescription'
import wcs from '@/scripts/libs/wcs'
const { manualPrescriptionIsEnabled, uploadPrescriptionIsEnabled } = wcs.getStoreConfig()

interface IPrescription {
  savePrescriptionInStorage: (_prescriptionObject: PrescriptionObject) => void
  getPrescriptionInStorage: () => PrescriptionObject | null
  clearPrescriptionInStorage: () => void
  clearPrescription: (_orderId: number, _orderItemId: number) => void
  savePrescription: (
    _prescriptionObject: PrescriptionObject,
    _orderId: number,
    _orderItemId: number
  ) => void
  isUploadPrescriptionObject: (_prescriptionObject: PrescriptionObject) => boolean

  mapPrescriptionFlowsFromCookies: () => PrescriptionFlow[]

  getPrescription: (_orderId: number, _orderItemId: number) => Promise<PrescriptionObject>

  enablePrism: () => boolean
}

class Prescription implements IPrescription {
  private readonly PRESCRIPTION_OBJECT_STORAGE_KEY = 'prescriptionObject'
  savePrescriptionInStorage = (prescriptionObject: PrescriptionObject) => {
    const encodedData = JSON.stringify(prescriptionObject)
    sessionStorage.setItem(this.PRESCRIPTION_OBJECT_STORAGE_KEY, encodedData)
    localStorage.setItem(this.PRESCRIPTION_OBJECT_STORAGE_KEY, encodedData)
  }
  getPrescriptionInStorage = (): PrescriptionObject | null => {
    const prescriptionFileInSessionStorage = sessionStorage.getItem(
      this.PRESCRIPTION_OBJECT_STORAGE_KEY
    )
    const prescriptionFileInLocalStorage = localStorage.getItem(
      this.PRESCRIPTION_OBJECT_STORAGE_KEY
    )
    if (prescriptionFileInSessionStorage) {
      return JSON.parse(prescriptionFileInSessionStorage)
    } else if (prescriptionFileInLocalStorage) {
      return JSON.parse(prescriptionFileInLocalStorage)
    }
    return null
  }
  clearPrescriptionInStorage = () => {
    sessionStorage.removeItem(this.PRESCRIPTION_OBJECT_STORAGE_KEY)
    localStorage.removeItem(this.PRESCRIPTION_OBJECT_STORAGE_KEY)
  }

  clearPrescription = async (orderId: number, orderItemId: number) => {
    await WCSPrescription.clearPrescription(orderId, orderItemId)
  }

  savePrescriptionToAvoidServerCall = async (
    prescriptionObject: PrescriptionObject,
    orderId: number,
    orderItemId: number
  ) => {
    const prescriptionWithId = {
      ...prescriptionObject,
      order: orderId,
      orderItem: orderItemId,
    }

    const prescriptionObj = JSON.stringify(prescriptionWithId)
    const prescriptionArray = [prescriptionObj]
    const prescriptionArrayString = JSON.stringify(prescriptionArray)
    const existingItem = sessionStorage.getItem('prescriptionObjWithId') ?? ''

    if (!existingItem) {
      return
    }

    const parsed = JSON.parse(existingItem)

    if (!parsed) {
      sessionStorage.setItem('prescriptionObjWithId', prescriptionArrayString)
      return
    }

    if (!Array.isArray(parsed)) {
      return
    }

    const find = parsed.find((element) => {
      return element.orderId === orderId && element.orderItemId === orderItemId
    })

    if (find) {
      return
    }

    const result = parsed.push(prescriptionObj)

    const stringfyResult = JSON.stringify(result)

    sessionStorage.setItem('prescriptionObjWithId', stringfyResult)
  }

  savePrescription = async (
    prescriptionObject: PrescriptionObject,
    orderId: number,
    orderItemId: number
  ) => {
    this.savePrescriptionToAvoidServerCall(prescriptionObject, orderId, orderItemId)

    if (this.isUploadPrescriptionObject(prescriptionObject)) {
      await this.saveUploadPrescription(prescriptionObject, orderId, orderItemId)
    } else if (this.isManualPrescriptionObject(prescriptionObject)) {
      await this.saveManualPrescription(
        prescriptionObject as ManualPrescriptionObject,
        orderId,
        orderItemId
      )
    }
  }
  private saveUploadPrescription = async (
    prescriptionFile: UploadPrescriptionObject,
    orderId: number,
    orderItemId: number
  ) => {
    await WCSPrescription.saveUploadPrescription(prescriptionFile, orderId, orderItemId)
  }
  private saveManualPrescription = async (
    manualPrescription: ManualPrescriptionObject,
    orderId: number,
    orderItemId: number
  ) => {
    await WCSPrescription.saveManualPrescription(manualPrescription, orderId, orderItemId)
  }
  isUploadPrescriptionObject = (
    prescriptionObject: PrescriptionObject
  ): prescriptionObject is UploadPrescriptionObject => {
    if (isEmpty(prescriptionObject) || !prescriptionObject) {
      return false
    }
    return (
      'fileData' in prescriptionObject ||
      prescriptionObject.prescriptionFlow === PrescriptionFlow.UPLOAD
    )
  }

  isManualPrescriptionObject = (
    prescriptionObject: PrescriptionObject
  ): prescriptionObject is ManualPrescriptionObject => {
    if (isEmpty(prescriptionObject) || !prescriptionObject) {
      return false
    }
    return prescriptionObject.prescriptionFlow === PrescriptionFlow.MANUAL
  }

  //TODO: remove this when we understand why get function from cookies.ts doesn't work
  getCookie = (cname: string) => {
    const name = cname + '='
    const ca = document.cookie.split(';')
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i]
      while (c.charAt(0) == ' ') {
        c = c.substring(1)
      }
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length)
      }
    }
    return ''
  }

  mapPrescriptionFlowsFromCookies = () => {
    const prescriptionIsEnabled = this.getCookie(PRESCRIPTION_ENABLED)
    const isPrescriptionManualEnabled = this.getCookie(PRESCRIPTION_MANUAL_ENABLED)
    const isPrescriptionUploadEnabled = this.getCookie(PRESCRIPTION_UPLOAD_ENABLED)

    if (prescriptionIsEnabled && !manualPrescriptionIsEnabled && !uploadPrescriptionIsEnabled) {
      if (prescriptionIsEnabled === TRUE_LOWERCASE) {
        return [PrescriptionFlow.MANUAL, PrescriptionFlow.UPLOAD]
      } else if (prescriptionIsEnabled === FALSE_LOWERCASE) {
        return []
      }
    } else {
      const result = []

      if (isPrescriptionManualEnabled === TRUE_LOWERCASE || manualPrescriptionIsEnabled) {
        result.push(PrescriptionFlow.MANUAL)
      }

      if (isPrescriptionUploadEnabled === TRUE_LOWERCASE || uploadPrescriptionIsEnabled) {
        result.push(PrescriptionFlow.UPLOAD)
      }

      return result
    }

    if (!prescriptionIsEnabled && !isPrescriptionManualEnabled && !isPrescriptionUploadEnabled) {
      return [PrescriptionFlow.MANUAL, PrescriptionFlow.UPLOAD]
    }

    return []
  }

  getPrescriptionInStorageToAvoidServerCall = (orderId: number, orderItemId: number) => {
    const sessionObj = sessionStorage.getItem('prescriptionObjWithId')
    if (!sessionObj) {
      return
    }

    const parsed = JSON.parse(sessionObj)

    if (!Array.isArray(parsed)) {
      return
    }

    const find = parsed.find((element) => {
      return element.orderId === orderId && element.orderItemId === orderItemId
    })

    if (!find) {
      return
    }

    delete find.orderItemId
    delete find.orderId

    return find
  }

  getPrescription = async (orderId: number, orderItemId: number) => {
    if (!wcs.isInOrdersHistoryPage()) {
      const prescriptionObjectInStorage = this.getPrescriptionInStorageToAvoidServerCall(
        orderId,
        orderItemId
      )
      if (prescriptionObjectInStorage && !isEmpty(prescriptionObjectInStorage)) {
        return prescriptionObjectInStorage
      }
    }

    const isOrderId = !!orderId && orderId !== ORDER_ID_DEFAULT_VALUE
    const isOrderItemId = !!orderItemId && orderItemId !== ORDER_ITEM_ID_DEFAULT_VALUE
    const areOrderNumbersDefined = isOrderId && isOrderItemId
    if (areOrderNumbersDefined) {
      const prescription = await WCSPrescription.getPrescription(orderId, orderItemId)
      this.savePrescriptionToAvoidServerCall(prescription, orderId, orderItemId)
      return prescription
    }

    return {} as PrescriptionObject
  }

  enablePrism = (): boolean => {
    return WCSPrescription.enablePrism()
  }
}

export default new Prescription()
